import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Snackbar
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import SearchIcon from '@mui/icons-material/Search'
import ItemMappingTable from '../components/dc/ItemMappingTable'
import { dcApi } from '../services/dc.api'
import { DCItemDiscount, MasterMedicine } from '../types/dc.types'

export default function DCItemMappingPage() {
  const { uploadId } = useParams<{ uploadId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [items, setItems] = useState<DCItemDiscount[]>([])
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  })

  // Fetch upload details
  const { data: uploadData, isLoading: isLoadingUpload, error: uploadError } = useQuery({
    queryKey: ['upload', uploadId],
    queryFn: () => dcApi.getUploadDetails(parseInt(uploadId!)),
    enabled: !!uploadId
  })

  // Match items mutation
  const matchMutation = useMutation({
    mutationFn: () => dcApi.matchItems({
      uploadId: parseInt(uploadId!),
      items: uploadData?.items || []
    }),
    onSuccess: (data) => {
      setItems(data.items)
      setSnackbar({
        open: true,
        message: `Matched ${data.matchStats.matched} of ${data.matchStats.total} items`,
        severity: 'success'
      })
    },
    onError: (err: Error) => {
      setSnackbar({
        open: true,
        message: `Match error: ${err.message}`,
        severity: 'error'
      })
    }
  })

  // Save items mutation
  const saveMutation = useMutation({
    mutationFn: () => dcApi.saveItems({
      uploadId: parseInt(uploadId!),
      items: items,
      deleteExisting: true
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dc-stats'] })
      setSnackbar({
        open: true,
        message: 'Items saved successfully!',
        severity: 'success'
      })
      setTimeout(() => navigate('/comparison'), 1500)
    },
    onError: (err: Error) => {
      setSnackbar({
        open: true,
        message: `Save error: ${err.message}`,
        severity: 'error'
      })
    }
  })

  // Search medicines query
  const { data: searchResults = [], isFetching: isSearching } = useQuery({
    queryKey: ['search-medicines', searchQuery],
    queryFn: () => dcApi.searchMedicines(searchQuery),
    enabled: searchQuery.length >= 2
  })

  // Manual match mutation
  const manualMatchMutation = useMutation({
    mutationFn: dcApi.manualMatch,
    onSuccess: (data, variables) => {
      // Update local state
      setItems(prev => prev.map((item, idx) => {
        if (idx === selectedItemIndex) {
          return {
            ...item,
            medicine_id: variables.medicineId,
            is_matched: true,
            match_confidence: 1.0,
            match_method: 'manual',
            matched_medicine_name: data.medicineName
          }
        }
        return item
      }))
      setSearchDialogOpen(false)
      setSearchQuery('')
      setSelectedItemIndex(null)
    }
  })

  // Auto-match on load
  useEffect(() => {
    if (uploadData?.items && uploadData.items.length > 0 && items.length === 0) {
      matchMutation.mutate()
    }
  }, [uploadData])

  const handleOpenSearch = (index: number) => {
    setSelectedItemIndex(index)
    setSearchQuery(items[index].raw_product_name || '')
    setSearchDialogOpen(true)
  }

  const handleSelectMedicine = (masterMedicine: MasterMedicine) => {
    if (selectedItemIndex !== null && items[selectedItemIndex].id) {
      manualMatchMutation.mutate({
        itemDiscountId: items[selectedItemIndex].id!,
        medicineId: masterMedicine.id
      })
    }
  }

  const handlePtrChange = (index: number, ptr: number) => {
    setItems(prev => prev.map((item, idx) => {
      if (idx === index) {
        return { ...item, ptr, ptr_source: 'manual' }
      }
      return item
    }))
  }

  if (isLoadingUpload) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (uploadError) {
    return (
      <Alert severity="error">
        Failed to load upload data. Please try again.
      </Alert>
    )
  }

  const matchedCount = items.filter(i => i.is_matched).length
  const totalCount = items.length
  const matchPercentage = totalCount > 0 ? (matchedCount / totalCount) * 100 : 0

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Item Mapping
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => matchMutation.mutate()}
            disabled={matchMutation.isPending}
          >
            {matchMutation.isPending ? 'Matching...' : 'Re-Match All'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || matchedCount === 0}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Items'}
          </Button>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2">
            Match Progress: {matchedCount} of {totalCount} items
          </Typography>
          <Box display="flex" gap={1}>
            <Chip
              size="small"
              label={`${matchPercentage.toFixed(0)}% matched`}
              color={matchPercentage >= 80 ? 'success' : matchPercentage >= 50 ? 'warning' : 'error'}
            />
            <Chip
              size="small"
              label={uploadData?.distributor_name || 'Unknown Distributor'}
              variant="outlined"
            />
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={matchPercentage}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Paper>

      {/* Item Mapping Table */}
      <Paper sx={{ p: 2 }}>
        {matchMutation.isPending ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Matching items to medicine master...</Typography>
          </Box>
        ) : (
          <ItemMappingTable
            items={items}
            onSearchClick={handleOpenSearch}
            onPtrChange={handlePtrChange}
          />
        )}
      </Paper>

      {/* Search Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Search Master Medicines
          {selectedItemIndex !== null && (
            <Typography variant="body2" color="textSecondary">
              For: {items[selectedItemIndex]?.raw_product_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            placeholder="Search by medicine name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: 2, mt: 1 }}
          />

          {isSearching ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          ) : searchResults.length === 0 ? (
            <Typography color="textSecondary" align="center">
              {searchQuery.length < 2 ? 'Type at least 2 characters to search' : 'No medicines found'}
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {searchResults.map((medicine: MasterMedicine) => (
                <Paper
                  key={medicine.id}
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => handleSelectMedicine(medicine)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography fontWeight="medium">{medicine.medicine_name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {medicine.pack_size} | {medicine.manufacturer_name}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      {medicine.ptr && (
                        <Typography variant="body2">PTR: Rs.{medicine.ptr}</Typography>
                      )}
                      {medicine.confidence && (
                        <Chip
                          size="small"
                          label={`${(medicine.confidence * 100).toFixed(0)}% match`}
                          color={medicine.confidence > 0.8 ? 'success' : 'default'}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
